/**
 * Validates email address format
 *
 * This is a more robust email validation that checks for:
 * - Basic email structure (local@domain.tld)
 * - No multiple @ symbols
 * - No whitespace
 * - Valid domain with at least one dot
 * - No consecutive dots
 *
 * @param email - The email string to validate
 * @returns true if email is valid, false otherwise
 */
export const isValidEmail = (email: string): boolean => {
  if (!email || typeof email !== 'string') {
    return false;
  }

  // Trim whitespace
  const trimmedEmail = email.trim();

  // Check for empty string after trim
  if (trimmedEmail === '') {
    return false;
  }

  // Check for whitespace within the email
  if (/\s/.test(trimmedEmail)) {
    return false;
  }

  // Check for exactly one @ symbol
  const atSymbolCount = (trimmedEmail.match(/@/g) || []).length;
  if (atSymbolCount !== 1) {
    return false;
  }

  // Basic email regex pattern
  // This pattern checks for:
  // - One or more chars that are not whitespace or @ (local part)
  // - A single @ symbol
  // - One or more chars that are not whitespace or @ (domain)
  // - A dot
  // - One or more chars that are not whitespace or @ (TLD)
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!emailRegex.test(trimmedEmail)) {
    return false;
  }

  // Additional validation: check for consecutive dots
  if (/\.\./.test(trimmedEmail)) {
    return false;
  }

  // Split by @ to validate local and domain parts separately
  const [localPart, domainPart] = trimmedEmail.split('@');

  // Local part should not start or end with a dot
  if (localPart.startsWith('.') || localPart.endsWith('.')) {
    return false;
  }

  // Domain part should not start or end with a dot
  if (domainPart.startsWith('.') || domainPart.endsWith('.')) {
    return false;
  }

  // Domain should have at least one dot
  if (!domainPart.includes('.')) {
    return false;
  }

  // TLD (after the last dot) should be at least 2 characters
  const tld = domainPart.split('.').pop();
  if (!tld || tld.length < 2) {
    return false;
  }

  return true;
};
